import { AuthTokenService } from '@packages/contexts/auth';
import { ConfigService } from '@packages/core/config';
import type { Container } from '@packages/core/container';
import { HttpClient as CoreHttpClient } from '@packages/core/http';
import { StorageService } from '@packages/core/storage';
import { getEnvConfig } from '@web/config';
import { DocsService } from '@web/vin-sessions/context/docs-session.service';
import { VinSessionService } from '@web/vin-sessions/context/vin-session.service';
import { GoogleAnalyticsService } from '@web/core/google-analytics/google-analytics.service';

import { AssetUploadsService } from '../asset-uploads.service';
import { HttpClient } from '../http-client';
import { OtpwService } from '../otpw.service';
import { PushNotificationsService } from '../push-notifications.service';

/**
 * Register platform primitives into the container as pre-built instances.
 * These must be seeded before any `resolve()` call because web services and
 * context services depend on HttpClient, which in turn depends on AuthTokenService
 * (already registered by `registerContextServices`).
 *
 * @param container - the container with core + context service factories registered
 */
export function registerWebPrimitives(container: Container): void {
    // Seed browser-global-dependent primitives as instances.
    const storageService = new StorageService(window.localStorage);
    const configService = new ConfigService(storageService, getEnvConfig());
    container.instance(StorageService, storageService);
    container.instance(ConfigService, configService);

    // Web's HttpClient subclass, registered under core's HttpClient token so all
    // domain services (which depend on core's HttpClient) resolve this instance.
    const authTokenService = container.resolve(AuthTokenService);
    const httpClient = new HttpClient(storageService, configService, authTokenService);
    container.instance(CoreHttpClient, httpClient);
}

/**
 * Register web-only services into the container using lazy `register()` factories.
 * These services are web-platform-specific and are not part of any shared package.
 *
 * @param container - the container with primitives already seeded
 */
export function registerWebServices(container: Container): void {
    container.register(AssetUploadsService, [CoreHttpClient]);
    container.register(DocsService, [CoreHttpClient]);
    container.register(GoogleAnalyticsService, [ConfigService]);
    container.register(OtpwService, [CoreHttpClient]);
    container.register(PushNotificationsService, [StorageService, ConfigService, CoreHttpClient]);
    container.register(VinSessionService, [CoreHttpClient]);
}
